import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/**
 * Pure greedy fit: how many ordered items fit into `availableWidth`, reserving
 * room for a "More" trigger of `moreWidth` only when at least one item must
 * overflow.
 *
 * @param availableWidth - px budget for the items (and "More" when needed)
 * @param itemWidths - ordered per-item widths in px
 * @param moreWidth - px width of the "More" trigger
 * @returns the number of leading items to render inline; the rest overflow
 */
export function computeInlineCount(
  availableWidth: number,
  itemWidths: number[],
  moreWidth: number,
): number {
  // Everything fits without a "More" trigger → all inline.
  const total = itemWidths.reduce((sum, w) => sum + w, 0);
  if (total <= availableWidth) return itemWidths.length;

  // Otherwise reserve space for "More" and fit as many as possible.
  const budget = availableWidth - moreWidth;
  let used = 0;
  let count = 0;
  for (const w of itemWidths) {
    if (used + w > budget) break;
    used += w;
    count += 1;
  }
  return count;
}

interface UseTabOverflowOptions<T> {
  /** Ordered items competing for inline space (e.g. additional assets). */
  items: T[];
  /** Stable key for an item, used to cache its measured width. */
  getKey: (item: T) => string;
}

interface UseTabOverflowResult<T> {
  /** Attach to the flex container the inline items + "More" render into. */
  containerRef: (el: HTMLDivElement | null) => void;
  /** Attach to each ghost (hidden) item to measure its intrinsic width. */
  measureRef: (key: string) => (el: HTMLElement | null) => void;
  /** Attach to the ghost "More" trigger to measure its width. */
  moreRef: (el: HTMLElement | null) => void;
  /** Items that fit inline. */
  inlineItems: T[];
  /** Items that spilled into the "More" overflow. */
  overflowItems: T[];
}

/**
 * "Priority+" overflow for a horizontal tab bar: render as many `items` inline
 * as fit the container, spilling the remainder into a "More" menu — recomputed
 * as the container resizes. "More" appears ONLY when something actually
 * overflows.
 *
 * The caller renders two rows: the real row (container + inline items + "More")
 * and a visually-hidden "ghost" row holding ALL items plus a "More" trigger,
 * tagged with `measureRef`/`moreRef`, so every item's intrinsic width is known
 * regardless of what's currently inline. The split is computed in a layout
 * effect (before paint) so the initial all-inline default never flashes.
 */
export function useTabOverflow<T>({
  items,
  getKey,
}: UseTabOverflowOptions<T>): UseTabOverflowResult<T> {
  const containerElRef = useRef<HTMLDivElement | null>(null);
  const widthsRef = useRef<Map<string, number>>(new Map());
  const moreWidthRef = useRef(0);

  // Default to all-inline; the layout effect corrects this before paint.
  const [inlineCount, setInlineCount] = useState(items.length);

  const recompute = useCallback(() => {
    const el = containerElRef.current;
    if (!el) return;
    const available = el.clientWidth;
    const orderedWidths = items.map(
      (item) => widthsRef.current.get(getKey(item)) ?? 0,
    );
    const next = computeInlineCount(
      available,
      orderedWidths,
      moreWidthRef.current,
    );
    setInlineCount((prev) => (prev === next ? prev : next));
  }, [items, getKey]);

  const containerRef = useCallback((el: HTMLDivElement | null) => {
    containerElRef.current = el;
  }, []);

  const measureRef = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      if (el) widthsRef.current.set(key, el.getBoundingClientRect().width);
    },
    [],
  );

  const moreRef = useCallback((el: HTMLElement | null) => {
    if (el) moreWidthRef.current = el.getBoundingClientRect().width;
  }, []);

  // Recompute before paint whenever the item set changes (widths are already
  // populated by the ghost refs in this same commit).
  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  // Recompute on container resize (e.g. the user drags the side panel).
  useEffect(() => {
    const el = containerElRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => recompute());
    observer.observe(el);
    return () => observer.disconnect();
  }, [recompute]);

  const safeCount = Math.min(inlineCount, items.length);
  return {
    containerRef,
    measureRef,
    moreRef,
    inlineItems: items.slice(0, safeCount),
    overflowItems: items.slice(safeCount),
  };
}
